// Setting → Product Group: แคตตาล็อกกลุ่มสินค้า (ผู้ใช้สร้างเอง)
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import type { ProductGroup } from "@contracts/api";
import { apiErrorMessage } from "@shared/api/client";
import { IconSearch } from "@shared/ui/icons";
import { createGroup } from "../api/settings-api";
import {
  useEnabledBrands,
  useGroups,
  useInvalidateSettings,
  useProducts,
} from "../api/use-settings";
import { ProductLogo } from "../components/product-logo";
import { pickColor } from "../model/setting-menus";

export function GroupListPage() {
  const navigate = useNavigate();
  const { data: groups, isPending } = useGroups();
  const { data: products } = useProducts();
  const { byId: enabled } = useEnabledBrands();
  const [q, setQ] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const invalidate = useInvalidateSettings();

  const create = useMutation({
    mutationFn: () =>
      createGroup({ name: name.trim(), color: pickColor(name.trim()), members: [] }),
    onMutate: () => setMsg("กำลังสร้าง…"),
    // สร้างเสร็จพาไปหน้า config ของกลุ่มนั้นเลย (ยังไม่มีอะไรให้ดูในรายการ)
    // ต้องล้าง cache ก่อน ไม่งั้นหน้าปลายทางอ่านรายการเก่าแล้วขึ้น "ไม่พบกลุ่มสินค้า"
    // (ของเดิมโหลดหน้าใหม่ทั้งหน้า จึงไม่เจอปัญหานี้)
    onSuccess: (g) => {
      invalidate();
      navigate(`/setting/group/${encodeURIComponent(g.id)}`);
    },
    onError: () => setMsg("สร้างกลุ่มไม่สำเร็จ กรุณาลองใหม่"),
  });

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (groups ?? []).filter((g) => !needle || g.name.toLowerCase().includes(needle));
  }, [groups, q]);

  /** ชื่อสมาชิกย่อ ๆ สำหรับการ์ดกลุ่ม */
  const memberNames = (g: ProductGroup) => {
    const names = g.members
      .map((c) => (products ?? []).find((p) => p.productCode === c)?.productEngName || `#${c}`)
      .filter(Boolean);
    // กลุ่มไม่จำเป็นต้องมีสินค้า — ใช้เป็นกล่องผูกเพจล้วน ๆ ก็ได้
    if (!names.length) return "กลุ่มว่าง — ผูกเพจได้เลย";
    return names.slice(0, 3).join(" · ") + (names.length > 3 ? ` · +${names.length - 3}` : "");
  };

  const doCreate = () => {
    if (!name.trim()) {
      setMsg("กรุณาตั้งชื่อกลุ่ม");
      return;
    }
    create.mutate();
  };

  return (
    <>
      <div className="content-head">
        <div>
          <div className="breadcrumb">Setting / Product Groups</div>
          <h1>Product Groups</h1>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-box grow-box">
          <IconSearch />
          <input
            type="text"
            placeholder="ค้นหาชื่อกลุ่ม..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <span className="muted">{items.length.toLocaleString()} กลุ่ม</span>
        <button type="button" className="btn-primary" onClick={() => setShowCreate((v) => !v)}>
          ＋&nbsp; สร้างกลุ่ม
        </button>
      </div>

      <div className="panel" hidden={!showCreate}>
        <h3>สร้างกลุ่มใหม่</h3>
        <p className="muted small-note">
          ตั้งชื่อกลุ่มก่อน แล้วไปเลือกสินค้า/รูป/เพจ ในหน้าตั้งค่าของกลุ่ม
        </p>
        <div className="page-row">
          <input
            type="text"
            placeholder="ชื่อกลุ่ม เช่น SYSTEMA ทั้งหมด"
            maxLength={120}
            value={name}
            autoFocus={showCreate}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doCreate()}
          />
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={doCreate}
            disabled={create.isPending}
          >
            สร้างกลุ่ม
          </button>
        </div>
        <p className="save-status muted">{msg}</p>
      </div>

      {isPending ? (
        <div className="panel">
          <p className="muted">กำลังโหลดข้อมูล…</p>
        </div>
      ) : (
        <div className="product-grid">
          {items.length ? (
            items.map((g) => (
              <div
                key={g.id}
                className="product-card clickable"
                onClick={() => navigate(`/setting/group/${encodeURIComponent(g.id)}`)}
              >
                <ProductLogo url={g.logoUrl} letter={g.name.charAt(0)} fallbackBg={g.color} />
                <div className="product-info">
                  <div className="product-name" title={g.name}>
                    {g.name}
                  </div>
                  <div className="product-sub">{memberNames(g)}</div>
                  <div className="product-meta">
                    <span className="code-chip">{g.members.length} สินค้า</span>
                    {enabled.has(g.id) && <span className="badge badge-blue">In My Job</span>}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="panel empty-state">
              <h3>ยังไม่มีกลุ่มสินค้า</h3>
              <p className="muted">
                สร้างกลุ่มเพื่อรวมหลายสินค้าเข้าด้วยกัน แล้ว Enable เข้า My Job
                ได้เหมือนสินค้าเดี่ยว
              </p>
            </div>
          )}
        </div>
      )}
      {create.isError && <p className="muted small-note">{apiErrorMessage(create.error)}</p>}
    </>
  );
}
